import {
    SDKFunction,
    ScanType,
    SdkError,
    SdkType,
    ShareLinkOptions,
    ShareLinkResult
} from '../interface';
import { assign, get, isDingTalkPcUA, asyncLoadScript, getMobileUA } from '@libs/shared';
import JsSdkBase from '../core/js-sdk-base';
import { jsSdkAuth, sdkLog, jsSdkFallback } from '../decorator';
import { sdkLogger, withSdkTimeout } from '../utils';
import { UPLOAD_FILE_COUNT, JSSDK_URL, SdkResponseCode } from '../constant';

/**
 * 钉钉SDK
 * @see 钉钉新版文档 https://open.dingtalk.com/document/development/jsapi-overview
 * @see 钉钉旧版文档 https://open.dingtalk.com/document/development/jsapi-overview-1
 * @see 新旧API对照 https://open.dingtalk.com/document/development/comparison--client-apis
 */
export default class DingTalkSdk extends JsSdkBase {
    constructor() {
        super();
        this.sdkType = SdkType.DingTalk;
    }

    /**
     * 加载钉钉sdk脚本
     */
    @sdkLog()
    loadScript() {
        return asyncLoadScript(JSSDK_URL.Dingtalk);
    }

    @sdkLog()
    init(config: Parameters<JsSdkBase['init']>[0]) {
        return super.init(config);
    }

    /**
     * 获取钉钉授权码
     * @see https://open.dingtalk.com/document/development/obtain-the-micro-application-exemption-authorization-code
     * @see 新版文档 https://open.dingtalk.com/document/development/jsapi-get-auth-code
     * @param integrateCorpId 企业id（注意该ID是从钉钉侧获取的）
     */
    @sdkLog()
    requestAuthCode(integrateCorpId: string) {
        return new Promise<string>((resolve, reject) => {
            window.dd?.runtime.permission.requestAuthCode({
                corpId: integrateCorpId,
                onFail: (err: any) => {
                    /**
                     * requestAuthCode报错信息里的errorCode参数并不具体，具体错误码放在errorMessage里
                     * 所以通过正则提取，但是正则提取并不完全可靠，所以这里将原始错误信息也一并返回，业务侧可按需取用
                     * @see FXD-62326
                     */
                    const genericErrorCode = get(err, 'errorCode');
                    const specificErrorCode = get(err, 'errorMessage').match(/\d+/)?.[0];
                    const errorCode = specificErrorCode || genericErrorCode;
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            'dingtalk requestAuthCode fail',
                            { errorCode, rawError: err }
                        )
                    );
                },
                onSuccess: (info: any) => {
                    resolve(info.code);
                }
            });
        });
    }

    /**
     * 钉钉授权认证，唤起授权套件
     * @see https://open.dingtalk.com/document/orgapp/unified-licensing-suite-sdk
     */
    @sdkLog()
    authConfirm() {
        const { clientId } = this.jsSdkConfig || {};
        return import('dingtalk-design-libs/biz/openAuthMiniApp')
            .then(({ openAuthMiniApp }) => {
                return openAuthMiniApp({
                    panelHeight: 'percent75',
                    path: 'pages/home/home',
                    extraData: {
                        clientId,
                        rpcScope: 'Contact.User.Read',
                        fieldScope: 'Contact.User.mobile',
                        type: 0,
                        from: ''
                    }
                });
            })
            .then((res) => {
                const status = get(res, 'status');
                const authCode = get(res, 'result.authCode');
                const data =
                    status === 'ok'
                        ? {
                              code: authCode
                          }
                        : {
                              error: 'error'
                          };
                // 钉钉授权信息传递给后台，由业务侧处理
                return data;
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    /**
     * 钉钉jssdk网页鉴权
     * @see https://open.dingtalk.com/document/isvapp/develop-webapp-frontend
     * @param config 钉钉jssdk鉴权配置
     */
    @sdkLog()
    _config(config: JsSdkBase['jsSdkConfig']) {
        window.dd?.config(config);
    }

    _ready(callback: () => void) {
        window.dd?.ready(() => {
            sdkLogger.info(`${this.sdkType} ready callback`);
            callback();
        });
    }

    _error(callback: (err: any) => void) {
        window.dd?.error((err: any) => {
            sdkLogger.error(`${this.sdkType} error callback`, { err });
            callback(err);
        });
    }

    /**
     * 唤起联系管理员界面，开通应用或授权当前用户使用应用
     * @see https://open.dingtalk.com/document/isvapp/document-on-authorization-of-application-market-members#title-6mp-rga-cfm
     * @see https://www.npmjs.com/package/dingtalk-design-libs/v/0.0.24
     * @param id 应用appId（isv_app_id）
     * @param corpId 当前组织的corpId
     */
    @sdkLog()
    contactAdmin(id: string, corpId: string) {
        return import('dingtalk-design-libs/biz/contactAdminToUseApp').then(
            ({ contactAdminToUseApp }) => {
                return contactAdminToUseApp({ id, corpId });
            }
        );
    }

    /**
     * 图片预览
     * @see https://open.dingtalk.com/document/development/jsapi-preview-image
     * @param urls 图片地址列表
     * @param current 当前显示的图片链接，建议使用png、jpg格式图片链接
     *
     * 注意，经实测，安卓设备上，dd.previewImage 可能会出现能成功调用，但是无法返回结果的情况：
     * 即：既不会触发 success/onSuccess，也不会触发 fail/onFail，还不会触发 complete
     * 此时会导致 Promise 一直处于 pending 状态
     * iOS上调用成功后能正常通过触发 success 返回结果
     */
    @sdkLog()
    previewImage(urls: string[], current: string) {
        return new Promise((resolve, reject) => {
            window.dd?.previewImage({
                urls: urls, // 图片地址列表
                current: current, // 当前显示的图片链接
                success: (result: any) => resolve(result),
                fail: (err: any) =>
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    )
            });
        });
    }

    /**
     * 获取当前地理位置信息（单次定位）
     * @see https://open.dingtalk.com/document/development/jsapi-get-location
     * @param options 定位参数
     */
    @jsSdkAuth
    @jsSdkFallback((args) => [{ ...args[0], noIpLocate: 0 }])
    @sdkLog()
    getLocation(
        options: Parameters<JsSdkBase['getLocation']>[0]
    ): ReturnType<JsSdkBase['getLocation']> {
        return new Promise((resolve, reject) => {
            window.dd?.getLocation({
                targetAccuracy: options.accuracy,
                coordinate: 1,
                withReGeocode: true,
                useCache: false,
                success: (result: any) => {
                    const position = [result.longitude, result.latitude];
                    resolve([position, result]);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 扫条形码或二维码
     * @see https://open.dingtalk.com/document/development/jsapi-scan
     * @param options 扫码参数
     */
    @sdkLog()
    scanCode(options: Parameters<JsSdkBase['scanCode']>[0]) {
        let type = 'all';
        switch (options.type) {
            case ScanType.QrCode:
                type = 'qr';
                break;
            case ScanType.BarCode:
                type = 'bar';
                break;
        }
        return new Promise((resolve, reject) => {
            window.dd?.scan({
                type,
                success: (data: any) => {
                    resolve(data.text);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 选择图片
     * @see https://app.unpkg.com/dingtalk-jsapi@3.0.36/files/api/biz/util/uploadImage.d.ts
     * @see https://app.unpkg.com/dingtalk-jsapi@3.0.36/files/api/biz/util/uploadImageFromCamera.d.ts
     * @param options 配置项
     */
    @jsSdkAuth
    @sdkLog()
    chooseImage(options: Parameters<JsSdkBase['chooseImage']>[0]) {
        const {
            watermark,
            compressed = false,
            onlyCamera = false,
            maxFileCount = UPLOAD_FILE_COUNT.SINGLE
        } = options;

        return new Promise<File[]>((resolve, reject) => {
            if (onlyCamera) {
                const cfg = {
                    compression: !!compressed, // (是否压缩，默认为true)
                    onSuccess: (result: any) => resolve(result),
                    onFail: (err: any) =>
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        )
                };
                if (watermark) {
                    assign(cfg, {
                        stickers: {
                            // 水印信息
                            time: watermark.time,
                            username: watermark.username,
                            address: watermark.address,
                            dateWeather: watermark.dateWeather
                        }
                    });
                }
                window.dd?.biz.util.uploadImageFromCamera(cfg);
            } else {
                window.dd?.biz.util.uploadImage({
                    multiple: maxFileCount > UPLOAD_FILE_COUNT.SINGLE, // 是否多选，默认false
                    max: maxFileCount, // 最多可选个数
                    compression: !!compressed, // 此接口在文档里并没有出现，可能是属于隐藏参数，是钉钉那边的相关技术人员告知的
                    onSuccess: (result: any) => resolve(result),
                    onFail: (err: any) =>
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        )
                });
            }
        });
    }

    /**
     * DING 一下消息
     * @see https://open.dingtalk.com/document/development/jsapi-create-ding
     * @see https://open.dingtalk.com/document/development/jsapi-create-ding-for-pc
     * @see createDing API参数 https://app.unpkg.com/dingtalk-jsapi@3.0.42/files/api/biz/ding/create.d.ts
     * @see createDingForPc API参数 https://app.unpkg.com/dingtalk-jsapi@3.0.42/files/api/biz/ding/post.d.ts
     * @param options 配置
     */
    @jsSdkAuth
    @sdkLog()
    notify(options: Parameters<JsSdkBase['notify']>[0]): ReturnType<JsSdkBase['notify']> {
        return new Promise((resolve, reject) => {
            const params = {
                ...options,
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            };
            if (getMobileUA()) {
                window.dd?.createDing({
                    ...params,
                    success: (result: { dingCreateResult?: boolean }) => {
                        resolve({ success: !!result.dingCreateResult });
                    }
                });
            } else {
                window.dd?.createDingForPC({
                    ...params,
                    /**
                     * PC端 DING 一下返回的结果和移动端不大一样，是返回 { isOk?: boolean }
                     * 该参数并未在官方 API 类型中说明，是自己试出来的，坑货
                     * @see FXD-38186
                     */
                    success: (result: { isOk?: boolean }) => {
                        resolve({ success: !!result.isOk });
                    }
                });
            }
        });
    }

    /**
     * 显示确认框
     * @see https://open.dingtalk.com/document/development/jsapi-confirm
     * @param config 配置项
     * @param config.message 弹窗文案
     * @param config.title 标题
     * @param config.buttonLabels 按钮文案
     */
    @sdkLog()
    confirm(config: Parameters<JsSdkBase['confirm']>[0]): Promise<boolean> {
        // 这里给title和button文案非空默认值，防止报错
        const {
            message,
            title = 'Tip',
            confirmButtonText = 'OK',
            cancelButtonText = 'Cancel'
        } = config;
        return new Promise((resolve, reject) => {
            window.dd?.confirm({
                title: title,
                content: message,
                confirmButtonText,
                cancelButtonText,
                /**
                 * 注意，钉钉官方文档里说 dd.confirm 的 success 返回的是 { "confirm": true }
                 * 实际上返回的是 { "buttonIndex": 0 }，被耍了！
                 * @param result.buttonIndex 0: 点击取消，1: 点击确定
                 */
                success: (result: { buttonIndex: 0 | 1 }) => {
                    if (result.buttonIndex === 1) {
                        resolve(true);
                    } else {
                        resolve(false);
                    }
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 显示警告框
     * @see https://open.dingtalk.com/document/development/jsapi-alert
     * @param options 警告框配置
     */
    @sdkLog()
    alert(options: Parameters<JsSdkBase['alert']>[0]) {
        return new Promise((resolve, reject) => {
            window.dd?.alert({
                content: options.msg,
                title: options.title || '',
                buttonText: options.buttonName || 'OK',
                success: (result: any) => resolve(result),
                fail: (err: any) =>
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    )
            });
        });
    }

    /**
     * 设置导航栏标题
     * @see https://open.dingtalk.com/document/development/jsapi-set-navigation-title
     * @param title 标题
     */
    @sdkLog()
    setPageTitle(title: string) {
        return new Promise((resolve, reject) => {
            window.dd?.setNavigationTitle({
                title,
                success: (result: any) => resolve(result),
                fail: (err: any) =>
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    )
            });
        });
    }

    /**
     * 打开目标页面
     * @see https://open.dingtalk.com/document/development/jsapi-open-link
     * @param url 目标地址
     */
    @jsSdkFallback()
    @sdkLog()
    openLink(url: string) {
        return new Promise((resolve, reject) => {
            window.dd?.openLink({
                url,
                success: (result: any) => resolve(result),
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    @sdkLog()
    downloadLink({ url }: { url: string }) {
        return this.openLink(url);
    }

    /**
     * 旋转屏幕
     * @see https://open.dingtalk.com/document/development/jsapi-rotate-screen-view
     */
    @sdkLog()
    screenRotate() {
        const result = new Promise((resolve, reject) => {
            window.dd?.rotateScreenView({
                showStatusBar: false, // 否显示statusbar
                clockwise: true, // 是否顺时针方向
                success: (result: any) => resolve(result),
                fail: (err: any) =>
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    )
            });
        });

        return withSdkTimeout({
            result,
            sdkType: this.sdkType,
            sdkApi: 'screenRotate',
            timeout: 3000
        });
    }

    /**
     * 重置旋转屏幕
     * @see https://open.dingtalk.com/document/development/jsapi-reset-screen-view
     */
    @sdkLog()
    resetScreenRotate() {
        const result = new Promise((resolve, reject) => {
            window.dd?.resetScreenView({
                success: (result: any) => resolve(result),
                fail: (err: any) =>
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    )
            });
        });

        return withSdkTimeout({
            result,
            sdkType: this.sdkType,
            sdkApi: 'resetScreenRotate',
            timeout: 3000
        });
    }

    @sdkLog()
    screenRotateBeforeFullscreen(fullscreen: boolean): Promise<null> {
        return new Promise((resolve) => {
            if (fullscreen) {
                this.screenRotate()
                    .then(() => {
                        setTimeout(() => {
                            resolve(null);
                        }, 200);
                    })
                    .catch(() => {
                        resolve(null);
                    });
            } else {
                this.resetScreenRotate().finally(() => {
                    resolve(null);
                });
            }
        });
    }

    /**
     * 关闭页面
     * @see https://open.dingtalk.com/document/development/jsapi-quit-page
     * @see https://open.dingtalk.com/document/development/jsapi-close-page
     */
    @sdkLog()
    closeWindow(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            /**
             * 存在钉钉PC客户端渲染移动端页面场景，而close不支持在钉钉PC客户端调用
             * 场景如：钉钉审批侧边栏
             */
            if (isDingTalkPcUA()) {
                window.dd?.quitPage({
                    success: (result: any) => resolve(result),
                    fail: (err: any) =>
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        )
                });
            } else {
                window.dd?.closePage({
                    success: (result: any) => resolve(result),
                    fail: (err: any) =>
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        )
                });
            }
        });
    }

    /**
     * 展示传入的经纬度位置
     * @see https://open.dingtalk.com/document/development/jsapi-open-location
     * @param params 地图参数（经纬度）
     */
    @jsSdkAuth
    @sdkLog()
    openMap(params: Parameters<JsSdkBase['openMap']>[0]) {
        const { lng, lat, address } = params;
        return new Promise((resolve, reject) => {
            window.dd?.openLocation({
                latitude: lat,
                longitude: lng,
                title: address,
                success: (result: any) => resolve(result),
                fail: (err: any) =>
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    )
            });
        });
    }

    /**
     * 禁用iOS Webview弹性效果
     * 该API在IOS上调用时，大概率没有返回结果，导致频繁触发给jssdk设置的超时限制，同时该API的返回结果并不重要，所以去掉Promise包装
     * @see https://open.dingtalk.com/document/orgapp/disable-webview-autoscaling-for-ios
     */
    @sdkLog()
    disableBounce() {
        window.dd?.disableWebViewBounce();
        return Promise.resolve();
    }

    hasFunction(functionName: SDKFunction): Promise<boolean> {
        const functionList = [SDKFunction.ChooseImage, SDKFunction.DownloadLink];
        return Promise.resolve(functionList.includes(functionName));
    }

    /**
     * 钉钉自定义转发到会话
     * PC 完成分享时 success 返回 非空数组或true，识别为「转发成功」;空数组及 fail 的 Cancel、“3” 表示「取消」
     * Mobile 唤起分享组件即触发 success 回调，所以无法区分「转发成功」与「取消」
     * @see https://open.dingtalk.com/tools/explorer/jsapi?id=11673
     * @param options 最终分享链接
     */
    @sdkLog()
    shareLink(options: ShareLinkOptions): Promise<ShareLinkResult | void> {
        const { url, title, description, imageUrl } = options;
        return new Promise<ShareLinkResult | void>((resolve, reject) => {
            window.dd?.share({
                url,
                title,
                content: description,
                image: imageUrl,
                type: 0,
                success: (res: any) => {
                    // Mac PC
                    if (Array.isArray(res)) {
                        resolve({
                            status: res.length > 0 ? 'success' : 'cancelled',
                            data: res
                        });
                        return;
                    }
                    // Windows PC
                    if (res === true) {
                        resolve({ status: 'success', data: res });
                        return;
                    }
                    // Mobile
                    resolve({ data: res });
                },
                fail: (err: any) => {
                    if (err.errorMessage === 'Cancel' || err.errorCode === '3') {
                        resolve({ status: 'cancelled', data: err });
                        return;
                    }
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }
}
